<template>
  <div class="departments-tree">
    <b-card no-body>
      <b-card-header>
        <b-card-title>Department Hierarchy</b-card-title>
        <div>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="outline-primary"
            size="sm"
            class="mr-1"
            @click="$router.push('/departments/list')"
          >
            <feather-icon
              icon="ListIcon"
              class="mr-50"
            />
            List View
          </b-button>
          <b-button
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
            size="sm"
            @click="showCreateModal"
          >
            <feather-icon
              icon="PlusIcon"
              class="mr-50"
            />
            Add Department
          </b-button>
        </div>
      </b-card-header>

      <b-card-body>
        <!-- Filters -->
        <b-row class="mb-2">
          <b-col
            cols="12"
            md="4"
          >
            <b-form-group>
              <b-form-checkbox
                v-model="showActiveOnly"
                switch
                @change="loadHierarchy"
              >
                Show Active Departments Only
              </b-form-checkbox>
            </b-form-group>
          </b-col>
          <b-col
            cols="12"
            md="8"
            class="text-right"
          >
            <b-button
              variant="outline-secondary"
              size="sm"
              @click="expandAll"
            >
              <feather-icon
                icon="PlusSquareIcon"
                class="mr-50"
              />
              Expand All
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              class="ml-1"
              @click="collapseAll"
            >
              <feather-icon
                icon="MinusSquareIcon"
                class="mr-50"
              />
              Collapse All
            </b-button>
          </b-col>
        </b-row>

        <!-- Tree View -->
        <div
          v-if="loading"
          class="text-center py-4"
        >
          <b-spinner variant="primary" />
          <p class="mt-2">
            Loading hierarchy...
          </p>
        </div>

        <div
          v-else-if="hierarchy.length === 0"
          class="text-center py-4"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
            class="text-muted"
          />
          <p class="mt-2 text-muted">
            No departments found
          </p>
        </div>

        <div
          v-else
          class="tree-container"
        >
          <tree-item
            v-for="department in hierarchy"
            :key="department.id"
            :department="department"
            :expanded-ids="expandedIds"
            @toggle="toggleExpand"
            @view="viewDepartment"
            @edit="editDepartment"
            @add-child="addChild"
            @delete="confirmDelete"
          />
        </div>
      </b-card-body>
    </b-card>

    <!-- Create/Edit Modal -->
    <department-form-modal
      v-model="showFormModal"
      :department="selectedDepartment"
      :mode="formMode"
      @submitted="handleFormSubmitted"
    />
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BButton,
  BFormGroup,
  BFormCheckbox,
  BSpinner,
} from 'bootstrap-vue'
import { mapState, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import TreeItem from './components/TreeItem.vue'
import DepartmentFormModal from './components/DepartmentFormModal.vue'

export default {
  name: 'DepartmentsTree',

  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BButton,
    BFormGroup,
    BFormCheckbox,
    BSpinner,
    TreeItem,
    DepartmentFormModal,
  },

  directives: {
    Ripple,
  },

  data() {
    return {
      showActiveOnly: false,
      expandedIds: new Set(),
      showFormModal: false,
      selectedDepartment: null,
      formMode: 'create',
    }
  },

  computed: {
    ...mapState('departments', ['hierarchy', 'loading']),
  },

  created() {
    this.loadHierarchy()
  },

  methods: {
    ...mapActions('departments', ['fetchHierarchy', 'deleteDepartment']),

    async loadHierarchy() {
      try {
        await this.fetchHierarchy(this.showActiveOnly)
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.message || 'Failed to load hierarchy',
            variant: 'danger',
          },
        })
      }
    },

    toggleExpand(id) {
      if (this.expandedIds.has(id)) {
        this.expandedIds.delete(id)
      } else {
        this.expandedIds.add(id)
      }
      // Force reactivity
      this.expandedIds = new Set(this.expandedIds)
    },

    expandAll() {
      const getAllIds = departments => {
        let ids = []
        departments.forEach(dept => {
          ids.push(dept.id)
          if (dept.children && dept.children.length > 0) {
            ids = ids.concat(getAllIds(dept.children))
          }
        })
        return ids
      }
      this.expandedIds = new Set(getAllIds(this.hierarchy))
    },

    collapseAll() {
      this.expandedIds = new Set()
    },

    showCreateModal() {
      this.formMode = 'create'
      this.selectedDepartment = null
      this.showFormModal = true
    },

    viewDepartment(id) {
      this.$router.push({ name: 'departments-view', params: { id } })
    },

    editDepartment(department) {
      this.formMode = 'edit'
      this.selectedDepartment = department
      this.showFormModal = true
    },

    addChild(parent) {
      this.formMode = 'create'
      this.selectedDepartment = {
        parent_id: parent.id,
      }
      this.showFormModal = true
    },

    confirmDelete(department) {
      this.$swal({
        title: 'Are you sure?',
        text: `Delete department "${department.name}"? This action cannot be undone.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        customClass: {
          confirmButton: 'btn btn-danger',
          cancelButton: 'btn btn-outline-secondary ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.handleDelete(department.id)
        }
      })
    },

    async handleDelete(id) {
      try {
        await this.deleteDepartment(id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success',
            icon: 'CheckIcon',
            text: 'Department deleted successfully',
            variant: 'success',
          },
        })
        this.loadHierarchy()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            icon: 'AlertTriangleIcon',
            text: error.response?.data?.message || 'Failed to delete department',
            variant: 'danger',
          },
        })
      }
    },

    handleFormSubmitted() {
      this.showFormModal = false
      this.selectedDepartment = null
      this.loadHierarchy()
    },
  },
}
</script>

<style lang="scss" scoped>
.tree-container {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.357rem;
}
</style>
